/**
 * ALL gameplay numbers live here (ARCHITECTURE §5). The sim must not hard-code magic numbers; add them here.
 *
 * Units: distances in yards, speeds in yd/s, accelerations in yd/s², durations in FRAMES (ticks @60 Hz) unless the
 * name ends in `Sec`. The sim multiplies per-second values by `DT` (1/60). Everything is a plain number so it is
 * identical on every peer. Changing a value changes the simulation: online peers must run the same values.
 * `SIM_CONTENT_HASH` (./content.ts) hashes every export of this module and the server/peers refuse to pair
 * builds whose hashes differ. Every group is deep-frozen at load; a live tuning panel must be OFFLINE-ONLY and
 * edit a copy.
 *
 * Frame-data conventions (mirrors design/handegg.pen → "03 Animation"):
 *   startup  = frames before the move can hit/act (frame 1 is the first frame in the state)
 *   active   = frames the hitbox / effect is live
 *   recovery = frames after active before the player can act again (whiff recovery may be longer)
 * A window like `windowStart: 7, windowEnd: 22` is INCLUSIVE and counted in frames since entering the state,
 * where the first frame in the state is frame 1.
 */
import { DT, HEAD_RADIUS } from './constants';
import type { CharacterIndex, CharacterStats } from './characters';
import { CHARACTERS } from './characters';
import { deepFreeze } from './freeze';

const DEG = Math.PI / 180;

// ── Physics ────────────────────────────────────────────────────────────────────────────────────────────
export const PHYSICS = deepFreeze({
  /** Player gravity. With JUMP_VELOCITY 13 the jump rises for 0.4 s (24 f) to an apex of 2.6 yd. */
  gravity: 32.5,
  /** Downward speed applied by Down while airborne (fast-fall), yd/s. */
  fastFallSpeed: 24,
  /** Terminal fall speed (normal). */
  maxFallSpeed: 22,
  /** Ball gravity (lower than players: floatier arcs for jump balls). */
  ballGravity: 20,
  /**
   * Wind: a horizontal acceleration (yd/s²) applied to the BALL IN FLIGHT only (players are unaffected).
   * The match value is `MatchConfig.wind` (server-chosen, |wind| ≤ maxWind; ranked = 0). The sim stores the
   * signed value for the current possession in `rules.windX` (flipped when the attack direction flips: per
   * possession in Drives, per half in Classic) so neither side is favoured. Laser Arm ignores wind.
   */
  maxWind: 3,
  /** Ball restitution on ground bounce (only matters for loose/dead balls; a pass hitting the ground is incomplete). */
  ballBounce: 0.35,
  /** Ground friction applied to a rolling ball, per second (v *= 1 - friction*DT). */
  ballGroundFriction: 3,
  /** Ball restitution off goalposts (DOINK). */
  ballPostBounce: 0.55,
  /** Spin imparted per yd/s of throw speed (rad/s per yd/s). */
  ballSpinPerSpeed: 0.9,
  /** Head-vs-head separation stiffness (fraction of overlap resolved per tick, 0–1). */
  bodyPushResolve: 0.5,
} as const);

// ── Movement ───────────────────────────────────────────────────────────────────────────────────────────
export const MOVE = deepFreeze({
  /** Hop-run top speed. */
  runSpeed: 7.5,
  runAccel: 60,
  runDecel: 80,
  /** Pre-snap shuffle speed (behind the line). */
  shuffleSpeed: 2.5,
  airAccel: 30,
  airMaxSpeed: 8,
  /** Hop cadence while running (frames per hop) — the sim exposes the hop phase so render/audio stay in sync. */
  hopPeriodFrames: 16,
  /** Visual hop height (render only; the sim's collision head stays on the ground). */
  hopHeight: 0.28,
  jumpVelocity: 13,
  /** Frames of ground squash before take-off (0 = instant like Head Soccer). */
  jumpSquatFrames: 0,
  landingLagFrames: 4,
  /** Two presses of the same direction within this many frames = dash. */
  doubleTapWindow: 18,
  dashSpeed: 14,
  dashFrames: 16,
  dashRecoveryFrames: 6,
  /** Stutter back-step speed (cancel of a dash). */
  backstepSpeed: 6,
  /**
   * Hesitation (carrier, LIVE): hold NO direction for `hesitationMinStopFrames`..`hesitationMaxStopFrames`
   * frames while grounded, then press a direction → `hesitationBurstFrames` of speed × `hesitationBurstMul`.
   * A stop longer than the max is just standing still (no burst). Free (no stamina).
   */
  hesitationMinStopFrames: 6,
  hesitationMaxStopFrames: 24,
  hesitationBurstMul: 1.25,
  hesitationBurstFrames: 10,
  /** Truck (crouch) movement speed multiplier while charging. */
  truckCrawlMul: 0.55,
  /** Truck burst speed on release. */
  truckBurstSpeed: 13,
  /** Carrier speed multiplier while holding the ball (QB behind LOS and runners). */
  carrierSpeedMul: 0.96,
  /**
   * Inputs pressed up to this many frames before they are allowed are buffered (InputHistory.buffer). The buffer
   * does NOT count down during hit-stop, so a press made during a freeze fires on the first frame after it.
   */
  inputBufferFrames: 6,
  /**
   * Chord window for SPIN (▼ + Action): ONE definition everywhere — the two press ticks differ by at most this
   * many ticks (presses on ticks t and t+3 chord, t and t+4 do not), both presses still buffered, both held. When
   * the first press already started STIFF_ARM / CHARGING_THROW (frame 1 = its press tick), a second press with that
   * gap — i.e. on frames 1..chordWindowFrames+1 of the state — cancels it into SPIN. Pump fake therefore needs the ▼
   * press more than `chordWindowFrames` ticks after the Action press that began the charge. ▼ FIRST (TRUCK_CHARGE):
   * the crouch is the chord's first half — any Action press while ▼ is still held spins, and one that misses (▼
   * came up on its tick) is dropped, never left to fire as a throw (QA round 3: a human ▼…Action is often 4–6 ticks).
   */
  chordWindowFrames: 3,
} as const);

// ── Frame data ─────────────────────────────────────────────────────────────────────────────────────────
export interface MoveFrames {
  readonly startup: number;
  readonly active: number;
  readonly recovery: number;
}

export const FRAMES = deepFreeze({
  /** Jump: rises for ~24 f with PHYSICS.gravity/MOVE.jumpVelocity. */
  jump: { squat: MOVE.jumpSquatFrames, landingLag: MOVE.landingLagFrames },
  /**
   * Fake-jump cancel (carrier): press Down while RISING between frames 7 and 22 of the jump (inclusive) →
   * 3 f freeze in the air, 2 f slam to the ground, 8 f landing stumble. Costs STAMINA.fakeJumpCancel.
   */
  fakeCancel: { windowStart: 7, windowEnd: 22, freeze: 3, slam: 2, stumble: 8, slamSpeed: 40 },
  /**
   * Stutter step: press the OPPOSITE direction during frames 2–12 of a dash → back-step (STAMINA.stutter).
   * A carrier's stutter is the `stutter` feint; a defender's is the `fake_rush` feint.
   */
  stutter: { windowStart: 2, windowEnd: 12, backstep: 10, recovery: 4 },
  /** Spin move (▼+Action chord, carrier incl. QB mode): i-frames during `active`; long recovery on whiff. */
  spin: { startup: 3, active: 14, recovery: 10, whiffRecovery: 22 },
  /** Stiff-arm (carrier Action, Fist pose). */
  stiffArm: { startup: 4, active: 8, recovery: 14 },
  /** Truck (carrier holds Down on the ground): crouch, charge, burst on release. */
  truck: { startup: 5, minCharge: 8, maxCharge: 40, burst: 14, recovery: 10 },
  /** Dive grab (grounded defender Action). Fake dive = tap Down during frames 1..fakeWindowEnd of startup. */
  dive: { startup: 8, active: 10, recovery: 26, fakeWindowEnd: 6, fakeRecovery: 6 },
  /** Leap grab (airborne defender Action). Recovery = landing lag after the grab whiffs. */
  leapGrab: { startup: 3, active: 12, recovery: 18 },
  /** Throw charge (QB holds Action). Charge fraction = clamp((held - minCharge) / (maxCharge - minCharge), 0, 1). */
  throwCharge: { minCharge: 4, maxCharge: 45 },
  /** Throw release: the ball launches on the last startup frame. */
  throwRelease: { startup: 2, active: 1, recovery: 12 },
  /** Pump fake (Down while charging). Max PUMP_FAKES_PER_PLAY per play. */
  pumpFake: { recovery: 10 },
  /** Catch animation (ball secured). */
  catch: { secure: 8 },
  /** Tackled / down. */
  tackled: { down: 45 },
  /** Stumble after a whiffed lunge or a slam landing. */
  stumble: { frames: 8 },
  /**
   * Stun lengths. `spunPast`: a standing defender whose grab a spin dodged stumbles (he grabbed air) — the spin
   * carries the carrier past him instead of leaving him a point-blank dive.
   */
  stun: { bulldozerBounce: 30, truckKnockAside: 24, stiffArmed: 14, spunPast: 36 },
  celebrate: { frames: 90 },
  /** Hard count (offense presses Jump pre-snap). */
  hardCount: { recovery: 20 },
} as const);

export const PUMP_FAKES_PER_PLAY = 1;
export const HARD_COUNTS_PER_PLAY = 2;
/**
 * A feint counts as a successful bait if the opponent COMMITS within this many frames after it and the commitment
 * fails (exact rule per feint kind: ARCHITECTURE §5 "Bait rules").
 */
export const BAIT_WINDOW_FRAMES = 24;
/**
 * The HARD COUNT's bait window is longer: a flinch has to be seen, then the defender shuffles 1–2 yd to the line
 * before he is called offside (no human or CPU reaction fits in 24 f). An offside within this many frames of a
 * hard count is a successful bait ("WHAT A FAKE!").
 */
export const BAIT_WINDOW_HARD_COUNT_FRAMES = 60;

// ── Hitboxes (counter square, ARCHITECTURE §5) ─────────────────────────────────────────────────────────
/**
 * Offsets are in the attacker's FACING space: +ox = in front. Boxes are ellipses (rx, ry) centred at (ox, oy),
 * relative to the head centre. Hitboxes only decide CONTACT; WHO WINS a contact is decided by the explicit
 * `COUNTER` matrix below, which the sim applies before any other tackle logic.
 */
export const HITBOX = deepFreeze({
  /** Dive = LOW grab: only hits targets whose lowest point is below `maxTargetY`. */
  dive: { ox: 1.0, oy: 0.35, rx: 1.0, ry: 0.45, lungeSpeed: 12, maxTargetY: 0.9 },
  /** Leap = HIGH grab while airborne: only hits targets whose highest point is above `minTargetY`. */
  leap: { ox: 0.8, oy: 0.2, rx: 0.9, ry: 0.9, minTargetY: 0.8 },
  /**
   * Truck (TRUCK_CHARGE / TRUCK_BURST): the head stays on the ground (centre y = headRadius, never below) and its
   * HURTBOX squashes to an ellipse rx = headRadius, ry = headRadius × hurtboxScaleY, bottom on the ground (render
   * squashes the sprite the same way). Truck beating Leap comes from COUNTER, not from this box.
   * Knockback is applied to a defender the truck beats (yd/s, away from the carrier / upward).
   */
  truck: { hurtboxScaleY: 0.6, knockbackX: 8, knockbackY: 6 },
  /** Stiff-arm (Fist): during its active frames, beats a contact GRAB only (COUNTER); pushes the grabber away. */
  stiffArm: { ox: 1.1, oy: 0.1, rx: 0.6, ry: 0.6, push: 9 },
  /**
   * Plain contact tackle `grab`: heads touching while the defender is grounded and not in a recovery/stun state.
   * Disabled against an airborne carrier, SPIN i-frames and TRUCK_BURST (see COUNTER).
   */
  contactTackleEnabled: true,
} as const);

// ── Counter resolution (ARCHITECTURE §5 "Counter square") ──────────────────────────────────────────────
/** Carrier situation at the moment of contact (first matching row wins, top to bottom). */
export const CarrierClass = {
  /** SPIN with `iFrames > 0`. */
  SPIN: 0,
  /** Not grounded: AIRBORNE (a carrier jump is a hurdle), FAKE_FREEZE, CATCH in the air. */
  AIR: 1,
  /** TRUCK_BURST. */
  TRUCK_BURST: 2,
  /** TRUCK_CHARGE (crouched). */
  TRUCK_CHARGE: 3,
  /** STIFF_ARM during its active frames. */
  STIFF_ARM: 4,
  /** Anything else on the ground (incl. spin startup/recovery, stiff-arm startup/recovery, stumble). */
  GROUND: 5,
} as const;
export type CarrierClass = (typeof CarrierClass)[keyof typeof CarrierClass];

/** The defender's attack that made contact. */
export const LungeClass = {
  /** Plain head contact while grounded (HITBOX.contactTackleEnabled). */
  GRAB: 0,
  /** DIVE active frames (low). */
  DIVE: 1,
  /** LEAP_GRAB active frames (high, airborne). */
  LEAP: 2,
} as const;
export type LungeClass = (typeof LungeClass)[keyof typeof LungeClass];

export const CounterOutcome = {
  /** No contact is resolved (bodies just push apart). */
  NONE: 0,
  /** Carrier tackled (TACKLE counter 'none'), HITSTOP.tackle. */
  TACKLE: 1,
  /** Carrier tackled by the read that beats his move (TACKLE counter ≠ 'none'), HITSTOP.counterTackle. */
  COUNTER_TACKLE: 2,
  /** The lunge passes through: defender WHIFFs (HURDLE_CLEAR vs AIR, SPIN_DODGE vs SPIN). */
  WHIFF: 3,
  /** Carrier wins: defender knocked aside + stunned (TRUCK_BREAK vs truck, STIFF_ARM hit vs grab). */
  BREAK: 4,
} as const;
export type CounterOutcome = (typeof CounterOutcome)[keyof typeof CounterOutcome];

/**
 * COUNTER[carrierClass][lungeClass] → outcome. Applied BEFORE any other tackle logic. Then:
 *  - Bulldozer (ACTIVE): a TACKLE/COUNTER_TACKLE from a GROUNDED attack (GRAB or DIVE) becomes BULLDOZE (break,
 *    defender stunned FRAMES.stun.bulldozerBounce, special consumed). LEAP is unaffected ("airborne still works").
 *  - Mirage decoys are never in this table: any lunge touching a decoy pops it (WHIFF + DECOY_POP + bait).
 * The counter square (VISION §4): Hurdle beats Dive · Leap beats Hurdle · Truck beats Leap · Dive beats Truck.
 */
export const COUNTER: readonly (readonly CounterOutcome[])[] = deepFreeze([
  //            GRAB                     DIVE                           LEAP
  /* SPIN   */ [CounterOutcome.WHIFF, CounterOutcome.WHIFF, CounterOutcome.WHIFF],
  /* AIR    */ [CounterOutcome.NONE, CounterOutcome.WHIFF, CounterOutcome.COUNTER_TACKLE],
  /* T_BURST*/ [CounterOutcome.BREAK, CounterOutcome.COUNTER_TACKLE, CounterOutcome.BREAK],
  /* T_CHRG */ [CounterOutcome.TACKLE, CounterOutcome.COUNTER_TACKLE, CounterOutcome.BREAK],
  /* STIFF  */ [CounterOutcome.BREAK, CounterOutcome.TACKLE, CounterOutcome.TACKLE],
  /* GROUND */ [CounterOutcome.TACKLE, CounterOutcome.TACKLE, CounterOutcome.TACKLE],
]);

/** Allocation-free lookup. */
export const counterOutcome = (carrier: CarrierClass, lunge: LungeClass): CounterOutcome =>
  COUNTER[carrier]![lunge]!;

// ── Throwing ───────────────────────────────────────────────────────────────────────────────────────────
export interface ThrowCurvePoint {
  /** Charge fraction 0..1. */
  readonly t: number;
  /** Launch speed, yd/s (before the ARM multiplier). */
  readonly speed: number;
  /** Launch angle above horizontal, radians. */
  readonly angle: number;
}
/**
 * Field-goal / PAT toss curve: charge → (speed, angle), linear interpolation between points (before TOSS). Tuned so
 * a full-power toss (TOSS 3) still clears the crossbar from the opponent's ≈ 40 (≈ 51 yd to the end-line plane
 * from the line-up spot): field goals are a real 4th-down option from the 35–40 in, while a PAT from the 15 needs
 * a medium charge (a full one sails over the upright tops).
 */
export const THROW_CURVE: readonly ThrowCurvePoint[] = deepFreeze([
  { t: 0, speed: 11, angle: 20 * DEG },
  { t: 0.35, speed: 18, angle: 34 * DEG },
  { t: 0.7, speed: 25, angle: 43 * DEG },
  { t: 1, speed: 32.5, angle: 48 * DEG },
]);
/**
 * Forward-pass curve (owner: sim movement team; additive). In 1v1 the only receiver is the thrower ("throw &
 * chase", VISION §3), so a NORMAL pass is a raceable jump ball: its horizontal speed (≈ 6 → 10 yd/s before ARM)
 * stays close to a running head's, and the charge mostly buys height and distance (≈ 5 yd pop-over → ≈ 19 yd
 * bomb, apex ≈ 1.6 → 9 yd). THROW_CURVE above stays the curve for FG / PAT tosses (rules.playCall FG_TOSS /
 * PAT_TOSS), which must carry to the goalposts. Same charge fraction, ARM / Hail Mary multipliers and lob bonus.
 */
export const PASS_CURVE: readonly ThrowCurvePoint[] = deepFreeze([
  { t: 0, speed: 10, angle: 53.13 * DEG },
  { t: 0.35, speed: 13.04, angle: 57.53 * DEG },
  { t: 0.7, speed: 17.24, angle: 60.46 * DEG },
  { t: 1, speed: 21.47, angle: 62.24 * DEG },
]);
/**
 * Classic-format kick curves (charge fraction → speed, angle; × TOSS like a FG toss; same lob bonus and velocity
 * inheritance). A kick tumbles end over end and is LIVE where it lands (BallState.LOOSE): see KICK.
 *  - KICKOFF: from the kicker's 35. ≈ 16 yd squib at no charge → ≈ 60 yd carry (the receiver's goal line) at full.
 *  - PUNT: a high lob (≈ 2.8 s hang at full charge, ≈ 40 yd carry) so a receiver who drops at the snap can get
 *    under it — or rush the punter instead.
 *  - ONSIDE (kickoff with ▼ held at the release): a low skipping hop that bounces past the 10-yard free-ball line.
 */
export const KICKOFF_CURVE: readonly ThrowCurvePoint[] = deepFreeze([
  { t: 0, speed: 18, angle: 40 * DEG },
  { t: 0.5, speed: 27, angle: 45 * DEG },
  { t: 1, speed: 34, angle: 47 * DEG },
]);
export const PUNT_CURVE: readonly ThrowCurvePoint[] = deepFreeze([
  { t: 0, speed: 16, angle: 55 * DEG },
  { t: 0.5, speed: 24, angle: 60 * DEG },
  { t: 1, speed: 31, angle: 63 * DEG },
]);
export const ONSIDE_CURVE: readonly ThrowCurvePoint[] = deepFreeze([
  { t: 0, speed: 11, angle: 35 * DEG },
  { t: 1, speed: 14, angle: 35 * DEG },
]);

export const THROW = deepFreeze({
  /** Holding Up while releasing lobs the ball (adds to the angle). */
  lobBonusAngle: 12 * DEG,
  /** Ball spawn offset from the head centre (facing space) on release. */
  releaseOx: 0.9,
  releaseOy: 0.6,
  /** Forward pass must start with the QB's x behind the LOS (in attackDir terms) by at least this much. */
  losTolerance: 0,
  /**
   * Field-goal / PAT toss (rules.playCall = FG_TOSS or PAT_TOSS): launch speed uses `stats.tossMul` instead of
   * `armMul`, and the flight gets `ball.fgAttempt = true`. Same THROW_CURVE and charge.
   */
  tossUsesTossMul: true,
} as const);

/** Interpolate THROW_CURVE at charge fraction `t` (allocation-free; writes into `out`). */
export const sampleThrowCurve = (t: number, out: { speed: number; angle: number }): void => {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  for (let i = 1; i < THROW_CURVE.length; i++) {
    const b = THROW_CURVE[i]!;
    if (c <= b.t) {
      const a = THROW_CURVE[i - 1]!;
      const k = (c - a.t) / (b.t - a.t);
      out.speed = a.speed + (b.speed - a.speed) * k;
      out.angle = a.angle + (b.angle - a.angle) * k;
      return;
    }
  }
  const last = THROW_CURVE[THROW_CURVE.length - 1]!;
  out.speed = last.speed;
  out.angle = last.angle;
};

/** Interpolate PASS_CURVE at charge fraction `t` (allocation-free; writes into `out`). */
export const samplePassCurve = (t: number, out: { speed: number; angle: number }): void => {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  for (let i = 1; i < PASS_CURVE.length; i++) {
    const b = PASS_CURVE[i]!;
    if (c <= b.t) {
      const a = PASS_CURVE[i - 1]!;
      const k = (c - a.t) / (b.t - a.t);
      out.speed = a.speed + (b.speed - a.speed) * k;
      out.angle = a.angle + (b.angle - a.angle) * k;
      return;
    }
  }
  const last = PASS_CURVE[PASS_CURVE.length - 1]!;
  out.speed = last.speed;
  out.angle = last.angle;
};

/** Interpolate any throw/kick curve at charge fraction `t` (allocation-free; writes into `out`). */
export const sampleCurve = (
  curve: readonly ThrowCurvePoint[],
  t: number,
  out: { speed: number; angle: number },
): void => {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  for (let i = 1; i < curve.length; i++) {
    const b = curve[i]!;
    if (c <= b.t) {
      const a = curve[i - 1]!;
      const k = (c - a.t) / (b.t - a.t);
      out.speed = a.speed + (b.speed - a.speed) * k;
      out.angle = a.angle + (b.angle - a.angle) * k;
      return;
    }
  }
  const last = curve[curve.length - 1]!;
  out.speed = last.speed;
  out.angle = last.angle;
};

// ── Kicks (Classic format: kickoff / punt / onside tosses; ARCHITECTURE §5 "Classic format") ──────────────
export const KICK = deepFreeze({
  /** Kickoff line-up: the receiver starts this far downfield of the kick LOS (the kicker's 35 → his own 25). */
  receiverDepth: 40,
  /**
   * A kickoff cannot be started before this many line-up frames (instead of RULES.preSnapMinFrames): long enough
   * for the receiver to run up toward the restraining line if he smells an onside toss — or to stay deep. The
   * kicker sees where he went: that is the kickoff's mind game.
   */
  lineupFrames: 150,
  /**
   * Restraining line: until the kickoff is in the air the receiver stays at least this far from the LOS (he may
   * move freely, at run speed, behind it) and the kicker stays behind the LOS.
   */
  restrainYards: 10,
  /** After a kickoff snap the toss must be away within this many frames, else it is auto-kicked at full charge. */
  kickoffAutoFrames: 4 * 60,
  /** Charge fraction of an auto-kick (play clock expired / kickoffAutoFrames passed). */
  autoKickCharge: 1,
  /** A glove (any free glove) within this distance of a LOOSE kick picks it up; a touching head does too. */
  recoverRadius: 0.8,
  /** A LOOSE kick rolls out faster than a dead ball: ground friction per second (v *= 1 - friction·DT). */
  looseGroundFriction: 8,
  /** Horizontal speed a LOOSE kick keeps through each ground impact (bounces die out instead of skipping on). */
  looseBounceKeep: 0.7,
  /** A LOOSE kick nobody may / does pick up is dead this many frames after it first hits the ground. */
  looseDeadFrames: 3 * 60,
  /** A kick returner standing in his own end zone holding ▼ this many frames takes a knee: touchback. */
  kneelHoldFrames: 10,
} as const);

// ── Catching / swatting ────────────────────────────────────────────────────────────────────────────────
export const CATCH = deepFreeze({
  /** Ball centre within this distance of an Open/Reach glove = catch. */
  gloveCatchRadius: 0.75,
  /** Ball within this distance of a glove but outside the catch radius (or glove not open) = swat. */
  gloveSwatRadius: 1.0,
  /** A head touching the ball deflects it (swat); heads never catch. Uses head radius + BALL_RADIUS. */
  headSwat: true,
  /** Swat deflection speed added to the ball (yd/s). */
  swatImpulse: 7,
  /** Simultaneous catch on the same tick goes to the offense (NFL rule). */
  tieGoesToOffense: true,
  /**
   * A defender intercepts only while he is SET on the ball: it is in front of him (`facing × (ball.x − head.x)`
   * ≥ −interceptBehindSlop) and coming at him or being run down (`facing × (ball.vx − head.vx)` ≤ 0). A trailing
   * defender (the pass dropping over his shoulder) or one turned the wrong way can only swat it: incompletions.
   */
  interceptNeedsFacing: true,
  interceptBehindSlop: 0.15,
  /** Frames after release during which the thrower's own glove cannot touch the ball. */
  throwerIgnoreFrames: 10,
  /**
   * Frames after release during which a NON-thrower's passive glove (not lunging) cannot touch the ball, so a
   * defender parked at the line cannot glove a pass on its release tick (a lunge / head contact still can).
   */
  releaseGraceFrames: 3,
  /** A caught ball snaps to the glove; the glove holds it at this offset (facing space). */
  holdOx: 0.75,
  holdOy: -0.25,
} as const);

// ── Glove ──────────────────────────────────────────────────────────────────────────────────────────────
/** Glove local offsets (facing space, relative to head centre). The glove springs toward its target offset. */
export const GLOVE = deepFreeze({
  /** Fraction of the distance to the target covered per tick (0–1). Higher = snappier. */
  follow: 0.35,
  /** Max reach distance from the head centre. */
  maxReach: 1.7,
  idle: { ox: 0.8, oy: -0.4 },
  run: { ox: 0.9, oy: -0.3 },
  holdBall: { ox: 0.75, oy: -0.25 },
  windup: { ox: -0.6, oy: 0.4 },
  release: { ox: 1.3, oy: 0.5 },
  stiffArm: { ox: 1.4, oy: 0.1 },
  reachUp: { ox: 0.6, oy: 1.3 },
  dive: { ox: 1.5, oy: -0.2 },
  leap: { ox: 1.2, oy: 0.5 },
  truck: { ox: 0.9, oy: -0.1 },
} as const);

// ── Stamina & power ────────────────────────────────────────────────────────────────────────────────────
export const STAMINA = deepFreeze({
  max: 100,
  dash: 12,
  stutter: 20,
  fakeJumpCancel: 20,
  spin: 25,
  pumpFake: 15,
  fakeDive: 15,
  /**
   * Gate rule (one rule for sim, AI and HUD): a dash / feint / spin needs `stamina >= cost`. Otherwise the input
   * is refused (STAMINA_DENIED, the real move happens: no cancel, no dash) and nothing is spent.
   * `HudSnapshot.mustBeReal[p]` = stamina < minFeintCost.
   */
  minFeintCost: 15,
  /** Regen per second during LIVE play (full refill between plays). */
  regenPerSec: 3,
  /** Per-tick regen, precomputed. */
  regenPerTick: 3 * DT,
} as const);

export const POWER = deepFreeze({
  max: 100,
  /**
   * +1 per 2 yards gained by the carrier, counted only for NEW progress: each tick, if the carrier's x is beyond
   * `playBestX` (in attackDir), gain (x - playBestX) × perYard and move playBestX. `playBestX` resets to the LOS
   * at the snap (and to the catch spot on a catch), so running back and forth earns nothing.
   */
  perYard: 0.5,
  tackle: 12,
  catch: 10,
  interception: 20,
  bait: 15,
  swat: 5,
} as const);

// ── Hit-stop (frames the whole sim freezes; the play clock keeps counting) ─────────────────────────────
export const HITSTOP = deepFreeze({
  tackle: 10,
  /** A tackle won through the counter square (e.g. Leap stuffs a Hurdle). */
  counterTackle: 14,
  catch: 3,
  interception: 8,
  swat: 5,
  truckBreak: 6,
  stiffArm: 5,
  bulldozerBreak: 8,
  fakeSuccess: 4,
  touchdown: 12,
  postHit: 4,
} as const);

// ── Specials ───────────────────────────────────────────────────────────────────────────────────────────
/**
 * `cooldownFrames` (handegg-roster.md): once the effect ends (consumed or timed out), the special cannot be
 * re-armed (power cannot buy it again, specials/query.ts `specialAvailable`) until this many frames have passed,
 * counted from `specials/update.ts` the instant it enters SPENT. Blitz has no cooldown: it is capped by
 * `blitz.usesPerDrive` instead (one arm per drive, reset in state.ts `startDrive`).
 */
export const SPECIALS_TUNING = deepFreeze({
  /** Every activation telegraphs for 18 f (0.3 s) before the effect starts; the opponent can act. */
  telegraphFrames: 18,
  hailMary: { speedMul: 1.15, gravityMul: 1 / 1.3, hangTimeMul: 1.3, catchRadiusMul: 1.25, cooldownFrames: 20 * 60 },
  /** Bulldozer: the first tackle attempt bounces off (stunFrames on the ground, bounceSpeed away). */
  bulldozer: { durationFrames: 180, stunFrames: 40, bounceSpeed: 9, cooldownFrames: 25 * 60 },
  /**
   * Mirage: the decoy spawns at the carrier, runs the opposite way at runSpeed × decoySpeedMul for decoyFrames,
   * falls with PHYSICS.gravity (decoyVy) if spawned airborne, is NOT solid (never blocks or is tackled), and pops
   * (DECOY_POP + WHIFF + FAKE_SUCCESS bait 'mirage') when any lunge hitbox touches it — the lunger lost that
   * exchange (pursuit grace, as after any counter-square win). 1.5 s: long enough to bait a read (QA round 3).
   */
  mirage: { decoyFrames: 90, decoySpeedMul: 1.1, cooldownFrames: 22 * 60 },
  /**
   * Blitz (defense, PRE_SNAP only): pressing Special with full power ARMS it silently (SpecialPhase.ARMED, no
   * telegraph, power not spent, HUD shows it as ready). At the SNAP: SnapEvent.blitz = true (whistle), the 18 f
   * telegraph runs, power is spent, and RUSH_COUNT lasts only the telegraph (RUSH_GO reason 'blitz'). Limited to
   * `usesPerDrive` arms per drive (the opponent's possession), not a cooldown timer.
   */
  blitz: { rushFrames: 18, usesPerDrive: 1 },
  /** Rocket Boots (offense, carrier/QB): a flat speed burst for durationFrames (2 s), consumed by running its course. */
  rocketBoots: { durationFrames: 120, speedMul: 1.35, cooldownFrames: 25 * 60 },
  /**
   * Big Head (defense): head inflates for durationFrames (2.5 s) — bigger contact-grab reach, head-swat reach and
   * loose-ball recovery reach (all read `effectiveHeadRadius`, specials/query.ts), at the cost of a flat speed
   * penalty (the "slower — run past" counter).
   */
  bigHead: { durationFrames: 150, radiusMul: 1.5, speedMul: 0.85, cooldownFrames: 20 * 60 },
  /** Ground Pound (defense): an instant shockwave on activation — a grounded carrier within `radius` is stunned. */
  groundPound: { radius: 3.5, freezeFrames: 50, cooldownFrames: 30 * 60 },
  /** Double Jump (any): ACTIVE for the rest of the play; the first Jump press while airborne spends it. */
  doubleJump: { durationFrames: 300, jumpVelocityMul: 0.82, cooldownFrames: 18 * 60 },
  /** Magnet (any): for durationFrames (2.5 s), a loose or airborne ball within `radius` curves toward the glove. */
  magnet: { durationFrames: 150, radius: 6, pullAccel: 46, cooldownFrames: 35 * 60 },
} as const);

// ── Rules timing ───────────────────────────────────────────────────────────────────────────────────────
export const RULES = deepFreeze({
  /** Pre-snap play clock (8 s). At 0 the ball is auto-snapped (no stalling, no penalty). */
  playClockFrames: 8 * 60,
  /** Minimum frames after lining up before the offense may snap (lets both players see the formation). */
  preSnapMinFrames: 30,
  /** Rush count after the snap (3.0 s). Ends early if the offense crosses the LOS. */
  rushCountFrames: 3 * 60,
  /**
   * Offside: the DEFENDER's head centre past the LOS (toward the offense) by more than this, during PRE_SNAP or
   * RUSH_COUNT (before RUSH_GO). The OFFENSE is clamped behind the LOS in PRE_SNAP (its head centre cannot pass
   * it). Penalty: OFFSIDE_PENALTY_YARDS or half the distance to the goal, whichever is smaller; REPLAY_DOWN;
   * automatic first down if the new LOS reaches firstDownX (conversions replay from the new spot).
   */
  offsideTolerance: 0.1,
  /**
   * Play call (rules.playCall) is chosen AT THE SNAP: offense holding ▼ while pressing Action to snap = FG_TOSS
   * formation (any down; not on a 2-pt try). A PAT conversion is always PAT_TOSS. Only a flight with
   * `ball.fgAttempt` can score a field goal / PAT.
   */
  fgFormationHoldsDown: true,
  /**
   * Toss formations (FG_TOSS, PAT_TOSS, PUNT): an Action press that snapped the ball and is STILL held this many
   * frames later goes on into the toss charge (as on a kickoff: hold = charge, release = toss), credited from the
   * press. A shorter tap only snaps (the toss is then charged with a fresh press).
   */
  tossSnapHoldFrames: 12,
  /** Dead-ball time for the result banner and celebration (≈1.2 s). */
  deadFrames: 72,
  /** Longer dead time after scores. */
  scoreDeadFrames: 150,
  /** Conversion choice: offense presses Up = 2-pt play, Action = 1-pt toss. Defaults to the toss on timeout. */
  conversionChoiceFrames: 5 * 60,
  /** Game-over freeze before the sim stops changing. */
  gameOverFrames: 120,
  /**
   * Classic format: 2 halves × 2:00 of game clock. The clock runs during live play; after a tackle in bounds it
   * keeps running through the dead ball and the next line-up until the snap. Incompletes, scores, turnovers,
   * kicks, penalties and conversions stop it until the next snap (kicks: until the toss is away).
   */
  classicHalfFrames: 2 * 60 * 60,
  /** Classic: inside the last this-many frames of a half the clock stops after EVERY play (comebacks stay live). */
  classicClockStopsLastFrames: 30 * 60,
  /** Classic: the half-time pause (DEAD) before the second-half kickoff. */
  halftimeFrames: 4 * 60,
  /** A gain of at least this many yards on one play emits BIG_PLAY. */
  bigPlayYards: 20,
  /** PLAY_CLOCK_WARNING fires as the pre-snap play clock reaches 3, 2 and 1 seconds left. */
  playClockWarningSec: 3,
  /**
   * Anti-stall whistle: a live play (RUSH_COUNT + LIVE) lasting this long is blown dead at the carrier's spot (an
   * idle/AFK player can never freeze a match). Not reached in normal play.
   */
  maxLiveFrames: 20 * 60,
  /**
   * The LOS is never spotted closer than this (yd) to the attacked goal line (a carrier downed inside it, an
   * offside half-the-distance chain). More than the most a held ball (the front of the offense) can move in one
   * tick, so the ball can never break the goal-line plane on a tick on which contacts did not run (the snap / the
   * rush count): every goal-line snap is contestable.
   */
  minLosYardsToGoal: 1,
  /**
   * End the match as soon as the result is decided (Drives/OT): the trailing player cannot catch up even with a
   * TD + 2-pt on every remaining possession plus a pick-six on every opponent possession, or a TD on the final
   * possession makes the conversion irrelevant (walk-off).
   */
  endWhenDecided: true,
  /**
   * Overtime is capped: still tied after this many sudden-death rounds, the match ends and the player with more
   * yards from scrimmage wins (GAME_OVER.tiebreak 'yards'; OVERTIME_START.lastRound flags the final round).
   */
  maxOvertimeRounds: 4,
} as const);

// ── Camera hint (deterministic target; the renderer smooths it) ────────────────────────────────────────
export const CAMERA = deepFreeze({
  /** Look-ahead in the ball's direction of travel, yards. */
  lookAhead: 3.5,
  baseZoom: 1,
  /** Zoom out to keep a high ball in frame (zoom = max(minZoom, baseZoom - ballY * zoomOutPerYard)). */
  zoomOutPerYard: 0.04,
  minZoom: 0.6,
  /** Visible world width at zoom 1, yards (render guidance). */
  viewWidth: 28,
} as const);

// ── Character stat multipliers ─────────────────────────────────────────────────────────────────────────
/**
 * Index with `stat - 1` (stats are 1–5; 3 = 1.0 = the ranked baseline). ARM 4 (the Rookie) is a small step (1.03):
 * with pursuit making won exchanges mid-range gains, his arm was worth the most (CPU mirror: ≈ 60 % at Hard / All-Pro).
 */
export const STAT_MULTIPLIERS = deepFreeze({
  spd: [0.94, 0.97, 1, 1.03, 1.06],
  jmp: [0.96, 0.98, 1, 1.02, 1.04],
  arm: [0.9, 0.95, 1, 1.03, 1.12],
  tkl: [0.88, 0.94, 1, 1.1, 1.2],
  toss: [0.9, 0.95, 1, 1.05, 1.1],
} as const);

/** Ranked/competitive normalization: every stat is 3 and hitboxes are identical (only the special differs). */
export const RANKED_STATS: CharacterStats = deepFreeze({ spd: 3, jmp: 3, arm: 3, tkl: 3, toss: 3 });

/** Resolved per-player multipliers, copied into SimState at match start. */
export interface ResolvedStats {
  speedMul: number;
  jumpMul: number;
  armMul: number;
  tackleMul: number;
  tossMul: number;
  headRadius: number;
}

const mul = (table: readonly number[], stat: number): number =>
  table[Math.min(Math.max(Math.round(stat), 1), 5) - 1]!;

/** Resolve stat multipliers for a character. `normalized` = ranked rules (all 3s, base head radius). */
export const resolveStats = (character: CharacterIndex, normalized: boolean): ResolvedStats => {
  const def = CHARACTERS[character];
  const s = normalized ? RANKED_STATS : def.stats;
  return {
    speedMul: mul(STAT_MULTIPLIERS.spd, s.spd),
    jumpMul: mul(STAT_MULTIPLIERS.jmp, s.jmp),
    armMul: mul(STAT_MULTIPLIERS.arm, s.arm),
    tackleMul: mul(STAT_MULTIPLIERS.tkl, s.tkl),
    tossMul: mul(STAT_MULTIPLIERS.toss, s.toss),
    headRadius: HEAD_RADIUS * (normalized ? 1 : def.headScale),
  };
};

// ── Motion feel (owner: sim movement/physics/contact team) ─────────────────────────────────────────────
/**
 * Secondary feel numbers for the controller, integrator, ball and contact modules (the primary frame data and
 * speeds live in MOVE / FRAMES / PHYSICS / HITBOX above).
 */
export const MOTION = deepFreeze({
  /** QB walk speed (× run speed) while charging a throw on the ground. */
  chargeMoveMul: 0.45,
  /** Carrier speed (× run speed) during a stiff-arm. */
  stiffArmMoveMul: 0.85,
  /** Speed (× run speed) while recovering from a throw or a pump fake. */
  throwMoveMul: 0.35,
  /** Truck burst speed scales from this fraction (minimum charge) up to 1 (full charge) of MOVE.truckBurstSpeed. */
  truckBurstMinMul: 0.8,
  /** Leap grab: forward lunge speed (yd/s, facing) applied on its first frame (never slows a faster jump). */
  leapLungeSpeed: 8,
  /** Hesitation only counts a stop that began while moving at ≥ this fraction of the player's run speed. */
  hesitationMinSpeedFrac: 0.5,
  /** Dive recovery: belly-slide deceleration (yd/s²). */
  diveSlideDecel: 30,
  /** Tackled carrier: slide speed (yd/s) in the tackler's direction, decays with stunDecel. */
  tackledSlide: 2.5,
  /** Horizontal deceleration while stunned, stumbling, tackled or posing (yd/s²). */
  stunDecel: 18,
  /** An airborne ball within this distance (yd) of a free player's head is tracked by his open glove. */
  gloveTrackRange: 6,
  /**
   * Throw & chase: while his own FORWARD pass is in the air the thrower runs his route — run, dash and air speed ×
   * this (he knows where it is going; the defender has to read it). Hail Mary: × `chaseSpeedMulHailMary`.
   */
  chaseSpeedMul: 1.3,
  chaseSpeedMulHailMary: 1.45,
  /**
   * Kick coverage (Classic): once the receiver holds a kickoff / punt / onside kick, the kicker pursues at run, dash
   * and air speed × this — a single kicker covering an open field can still run a returner down after a miss.
   */
  coverageSpeedMul: 1.2,
  /**
   * Pursuit (scrimmage plays, LIVE): a player chasing the ball carrier from behind — the carrier is more than
   * `minGap` yd past him toward the carrier's goal and he faces that way — runs and drifts as fast as the CARRIER's
   * top speed × (1 + bonus + rampBonus × ramp) (never slower than his own; his dash stays a plain dash), the ramp
   * growing from 0 at `minGap` to 1 at `fullGap` yd. Relative to the carrier, so every character pairing closes at
   * the same rate. A won exchange gains yards instead of always going the distance: the beaten defender runs the
   * carrier down and the next exchange decides (so downs and distance matter). Applies to an interception return
   * too; kick coverage uses coverageSpeedMul. `graceFrames`: none of it for this long after an exchange he LOST
   * (PlayerSim.beatenTick) — QA round 3: a beaten defender was back on the carrier in 0.5–1.1 s, erasing the read.
   * `diveKeepsMomentum`: a pursuer's dive is a running dive — it keeps his speed (at most his run speed) through the
   * wind-up (a planted dive from behind could never reach a fleeing carrier); the lunge itself is a plain dive. The
   * carrier's answers: the hurdle, or a dash.
   */
  pursuit: { bonus: 0.1, rampBonus: 0.2, minGap: 1, fullGap: 8, graceFrames: 45, diveKeepsMomentum: true },
  /** A throw inherits this fraction of the thrower's velocity (running / jumping throws go further). */
  throwInheritVel: 0.5,
  /** Extra distance (yd) allowed in touch tests (heads touching, hitbox overlap). */
  contactSlop: 0.05,
  /** Upward pop (yd/s) of a defender bounced by Bulldozer / knocked by a stiff-arm. */
  bulldozeBounceVy: 5,
  stiffArmPopVy: 3,
  /** Held ball: nose tilt above horizontal toward the facing direction (radians). */
  heldBallTilt: 0.35,
  /** Clean pass: the nose turns toward the velocity at this rate × heading error (1/s). */
  ballNoseFollow: 12,
  /** Free tumble (after a swat / post / bounce): angular damping per second in the air. */
  ballAirSpinDamping: 0.4,
  /** Coulomb friction coefficient of a ground bounce (gives the angle-dependent kick and spin transfer). */
  ballGroundFrictionCoef: 0.45,
  /** A ground impact slower than this (yd/s) rests instead of bouncing. */
  ballRestSpeed: 0.8,
  /** Angular damping per second while the ball rocks / rolls on the ground. */
  ballGroundSpinDamping: 3,
  /** Minimum impact speed (yd/s) for a BALL_BOUNCE event. */
  ballBounceEventSpeed: 1.5,
  /** Restitution of a ball deflected off a glove or head (before CATCH.swatImpulse). */
  swatRestitution: 0.3,
  /** Tumble spin given to a deflected ball, rad/s per yd/s of tangential speed (× PHYSICS.ballSpinPerSpeed). */
  deflectSpinMul: 1,
  /** Maximum flight substep length (yd) near the goalposts, so a fast ball never tunnels through a post. */
  ballSubstepMax: 0.12,
  /** Hard cap on flight substeps per tick near the goalposts. */
  ballMaxSubsteps: 8,
  /** A grounded dead ball slower than this (yd/s, rad/s) goes to sleep (velocities zeroed). */
  ballSleepSpeed: 0.05,
  /** Ball tucked against the head (facing space) while the glove is busy with a stiff-arm (Fist). */
  tuckOx: -0.35,
  tuckOy: -0.45,
  /** A catch is "contested" when the other player's glove or head is within this distance (yd) of the ball. */
  contestRadius: 2,
  /** A swatter may touch the same flight again only once the ball is this much (yd) beyond his touch range. */
  swatRearm: 0.5,
  /** Spin move: forward speed (× the player's run speed) during startup + active frames. */
  spinSpeedMul: 1.3,
  /**
   * A defender BEATEN by a counter-square win (truck break, stiff-arm hit, Bulldozer bounce, a spin dodging his
   * grab) is non-solid and cannot contact-GRAB that carrier until the carrier is past him (heads apart by the
   * radii + contactSlop in the carrier's attack direction), or at most this many frames. Lunges still work.
   */
  beatenMaxFrames: 90,
  /** An airborne head resting on top of the other head slides off sideways at this speed (yd/s). */
  headSlideSpeed: 3,
} as const);

/**
 * Everything above in one object (READ-ONLY debug overlays, docs). The sim imports the groups directly.
 * Frozen: an online build must never change these at runtime (SIM_CONTENT_HASH would no longer describe them).
 */
export const TUNING = deepFreeze({
  PHYSICS,
  MOVE,
  FRAMES,
  HITBOX,
  THROW,
  THROW_CURVE,
  PASS_CURVE,
  KICKOFF_CURVE,
  PUNT_CURVE,
  ONSIDE_CURVE,
  KICK,
  CATCH,
  GLOVE,
  STAMINA,
  POWER,
  HITSTOP,
  SPECIALS_TUNING,
  RULES,
  CAMERA,
  COUNTER,
  STAT_MULTIPLIERS,
  PUMP_FAKES_PER_PLAY,
  HARD_COUNTS_PER_PLAY,
  BAIT_WINDOW_FRAMES,
  BAIT_WINDOW_HARD_COUNT_FRAMES,
  MOTION,
} as const);
